/**
 * Public registration form by token (/p/:token) — QR target on badge/receipt.
 * Scanned with any phone camera it shows the full filled-in form (printable).
 * Uploaded documents are never exposed here.
 */

import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';
import { getPublicRegistration } from '../publicRegistrationRepository';
import { RegistrationDocumentViewer } from '../components/RegistrationDocumentViewer';

const STATUS_COPY = {
  PRE_REGISTERED: { icon: Clock, text: 'Preinscripción recibida — pendiente de verificación', className: 'text-amber-700' },
  VERIFIED: { icon: CheckCircle2, text: 'Inscripción verificada', className: 'text-green-700' },
  REJECTED: { icon: XCircle, text: 'Inscripción observada — contacte a la comisión', className: 'text-red-700' },
} as const;

export function PublicRegistrationViewPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('nuevo') === '1';
  // ?recibo=<number> opens the receipt tab (link shared by WhatsApp)
  const receiptParam = Number(searchParams.get('recibo')) || undefined;
  const { data, isLoading, error } = useQuery({
    queryKey: ['public_registration', token],
    queryFn: () => getPublicRegistration(token as string),
    enabled: !!token,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <Loader2 className="size-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-100 px-4 text-center">
        <AlertTriangle className="size-10 text-gray-400" />
        <p className="text-gray-600">Inscripción no encontrada.</p>
      </div>
    );
  }

  const status = STATUS_COPY[data.registration_status];
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-gray-100 py-8 print:min-h-0 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-3xl flex-col items-center gap-2 px-4 text-center print:hidden">
        {isNew && data.registration_status === 'PRE_REGISTERED' && (
          <div className="w-full rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            <p className="font-semibold">¡Inscripción enviada!</p>
            <p>
              Guarde este enlace o tome captura del código QR. La comisión verificará sus documentos y pago.
            </p>
          </div>
        )}
        <p className="text-sm font-medium text-gray-700">{data.event.name}</p>
        <p className={`flex items-center gap-1.5 text-sm font-medium ${status.className}`}>
          <StatusIcon className="size-4" /> {status.text}
        </p>
      </div>

      <RegistrationDocumentViewer registration={data} token={token as string} variant="page" initialReceiptNumber={receiptParam} />
    </div>
  );
}
